/**
 * The time cards at the top of Home: the evening review once it's due (or
 * opened from its notification), and the carry-over for earlier days still
 * unreleased. Nothing renders while the firm has time tracking off.
 */
import { Bell, Clock } from "lucide-react";
import type { FC } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";

import { Button } from "@/components/ui/button";
import type { AgentScope } from "@/features/agents/types";

import { lawyerSelectors } from "../../../redux/lawyer/selectors";
import type { LawyerReducer } from "../../../redux/lawyer/lawyerSlice";
import { useTimeToday } from "../queries";
import { timePathFor } from "../routes";
import { formatHours } from "../utils";
import { CarryOverCard } from "./CarryOverCard";
import { EveningReviewCard } from "./EveningReviewCard";
import {
  carryOverDays,
  eveningReviewDate,
  nudgeLine,
  REVIEW_PARAM
} from "./homeModel";

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export const TimeHomeCards: FC<{ practice: AgentScope }> = ({ practice }) => {
  const enabled = useSelector((s: LawyerReducer) =>
    lawyerSelectors.selectTimeTrackingEnabled(s)
  );
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const today = useTimeToday(enabled);
  if (!enabled || !today.data) return null;

  const linkedParam = params.get(REVIEW_PARAM);
  const linked = linkedParam && ISO_DATE.test(linkedParam) ? linkedParam : null;
  const reviewing = eveningReviewDate(today.data, linked);
  const carry = carryOverDays(today.data, reviewing);
  const hoursToday = today.data.totals.hours;
  const showToday = !reviewing && hoursToday > 0;
  const nudge = nudgeLine(today.data.nudge);
  if (!reviewing && !carry.length && !showToday && !nudge) return null;

  return (
    <div className="mt-6 flex flex-col gap-4" data-testid="time-home-cards">
      {nudge ? (
        <div
          className="flex items-center gap-2.5 rounded-xl border border-warn-line bg-warn-soft px-4 py-3"
          data-testid="time-nudge"
        >
          <Bell className="size-3.75 text-warn" />
          <span className="flex-1 text-base">{nudge}</span>
          <Button
            size="xs"
            variant="link"
            className="h-auto px-1"
            onClick={() =>
              navigate(timePathFor({ date: today.data?.nudge?.oldest }))
            }
          >
            Review and release
          </Button>
        </div>
      ) : null}
      {reviewing ? (
        <EveningReviewCard
          key={reviewing}
          date={reviewing}
          practice={practice}
          onOpenTimesheet={(date) => navigate(timePathFor({ date }))}
        />
      ) : null}
      {carry.length ? (
        <CarryOverCard
          days={carry}
          practice={practice}
          onReview={() => navigate(timePathFor())}
        />
      ) : null}
      {showToday ? (
        <div
          className="flex items-center gap-2.5 rounded-xl border border-line-2 px-4 py-3"
          data-testid="time-today-line"
        >
          <Clock className="size-3.75 text-fg-3" />
          <span className="flex-1 text-base tabular-nums">
            Today so far:{" "}
            <span className="font-medium">{formatHours(hoursToday)}</span>{" "}
            captured
            {today.data.unreleased_today
              ? `, ${today.data.unreleased_today} unreleased`
              : ""}
          </span>
          <Button
            size="xs"
            variant="link"
            className="h-auto px-1"
            onClick={() => navigate(timePathFor())}
          >
            Open timesheet
          </Button>
        </div>
      ) : null}
    </div>
  );
};
